import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/track';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (!isHomePage) {
      setIsScrolled(true);
      return;
    }
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMoreOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const primaryLinks = [
    { name: 'Models', path: '/models' },
    { name: 'Showroom', path: '/showroom' },
    { name: 'Build Your Van', path: '/build' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Van Rentals', path: '/#van-rentals' },
  ];

  const moreLinks = [
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const allLinks = [...primaryLinks, ...moreLinks];

  const isActive = (path) => location.pathname === path;
  const isMoreActive = moreLinks.some((l) => isActive(l.path));

  const linkClass = (active) =>
    `px-3 py-2 text-[13px] font-semibold tracking-wide transition-all duration-200 relative group whitespace-nowrap ${
      isScrolled
        ? active ? 'text-gold' : 'text-navy hover:text-gold'
        : active ? 'text-gold' : 'text-white/90 hover:text-white'
    }`;

  const underline = (active) =>
    `absolute bottom-0 left-3 right-3 h-0.5 bg-gold transform origin-left transition-transform duration-300 ${
      active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
    }`;

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-2'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 xl:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 flex-shrink-0"
          >
            <img
              src="/logo.png"
              alt="Legend Conversion"
              className={`h-9 md:h-11 w-auto transition-all ${
                isScrolled ? 'brightness-0' : 'brightness-100'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {primaryLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`${linkClass(isActive(link.path))} ${link.name === 'Van Rentals' ? 'hidden xl:block' : ''}`}
              >
                {link.name}
                <span className={underline(isActive(link.path))} />
              </Link>
            ))}

            {/* More Dropdown */}
            <div ref={moreRef} className="relative">
              <button
                data-testid="nav-more-dropdown"
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                className={`${linkClass(isMoreActive)} flex items-center gap-1 cursor-pointer`}
              >
                More
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isMoreOpen ? 'rotate-180' : ''}`} />
                <span className={underline(isMoreActive)} />
              </button>

              {isMoreOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-fade-in z-50">
                  <Link
                    to="/#van-rentals"
                    data-testid="nav-more-van-rentals"
                    className="xl:hidden block px-4 py-2.5 text-sm font-medium transition-colors text-navy hover:text-gold hover:bg-gray-50"
                  >
                    Van Rentals
                  </Link>
                  {moreLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                        isActive(link.path)
                          ? 'text-gold bg-gold/5'
                          : 'text-navy hover:text-gold hover:bg-gray-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA & Phone */}
          <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
            <a
              href="tel:6823691500"
              data-testid="phone-link"
              onClick={() => track('phone_click', { location: 'header_desktop' })}
              className={`flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-navy' : 'text-white'
              }`}
            >
              <Phone className="w-4 h-4" />
              (682) 369-1500
            </a>
            <Link to="/build">
              <Button
                data-testid="header-cta-btn"
                className="bg-gold text-navy hover:bg-gold-hover font-bold tracking-wider px-6"
              >
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${
              isScrolled ? 'text-navy' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t animate-fade-in"
          >
            <nav className="flex flex-col py-4">
              {allLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-6 py-3 text-base font-medium border-l-4 transition-all ${
                    isActive(link.path)
                      ? 'border-gold text-gold bg-gold/5'
                      : 'border-transparent text-navy hover:border-gold/50 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="px-6 py-4 border-t mt-2">
                <a
                  href="tel:6823691500"
                  onClick={() => track('phone_click', { location: 'header_mobile_menu' })}
                  className="flex items-center gap-2 text-navy font-medium mb-4"
                >
                  <Phone className="w-5 h-5 text-gold" />
                  (682) 369-1500
                </a>
                <Link to="/build" className="block">
                  <Button className="w-full bg-gold text-navy hover:bg-gold-hover font-bold">
                    Get a Quote
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
